import { pickT } from "@repo/shared";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { usePublicSession } from "@/lib/context/public-session-context";
import { useProductCosts } from "@/lib/hooks/use-flow-instances";
import { useCostOverrideEditor } from "../cost-override";
import { ProductCostsWidget } from "../product-costs-widget";
import { T } from "./global-product-costs.i18n";

type GlobalProductCostsProps = {
  instanceId: string;
  config: Record<string, unknown>;
  language: string;
  /** QL-20 — a locked instance (or read-only viewer) shows overrides but never edits. */
  locked?: boolean;
};

export function GlobalProductCosts({
  instanceId,
  config,
  language,
  locked = false,
}: GlobalProductCostsProps) {
  const t = pickT(T, language);
  const { isPublic } = usePublicSession();
  const editor = useCostOverrideEditor(isPublic ? null : instanceId, { locked });
  const { productCosts, isLoading } = useProductCosts(instanceId, language);
  const data = productCosts?.global;

  if (isLoading) return null;

  return (
    <VisualizationContainer config={config}>
      {!data || data.products.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
          {t.noProductCostsAvailable}
        </div>
      ) : (
        <ProductCostsWidget
          products={data.products}
          costTypes={data.costTypes}
          onEditCost={editor.canReset ? editor.edit : undefined}
          canOverride={editor.canEdit}
        />
      )}
      {editor.dialog}
    </VisualizationContainer>
  );
}
